import { useCallback, useMemo, useState } from "react";
import type { StoredLastVisit } from "./lastVisit.lib";
import { resolveLastVisit } from "./lastVisit.lib";

interface UseLastVisitParams {
    /** The visit stored for this page and user, wherever the page keeps it. */
    stored: StoredLastVisit | undefined;
    /** Writes this visit back; should do nothing until the user is known. */
    save: (visit: StoredLastVisit) => void;
}

/**
 * The current visit's "Since your last visit" start, plus `rememberVisit`,
 * which stores this visit so the next one starts here. Storage is the
 * caller's, so the workspace and organization pages can each keep their own.
 */
export function useLastVisit({ stored, save }: UseLastVisitParams) {
    // oxlint-disable-next-line react/hook-use-state -- A visit's start is read once and never updated.
    const [now] = useState(() => new Date());
    const since = useMemo(
        () => resolveLastVisit({ stored, now }),
        [stored, now],
    );
    const rememberVisit = useCallback(
        () =>
            save({
                lastSeenAt: now.toISOString(),
                since: since.toISOString(),
            }),
        [save, now, since],
    );
    return { since, rememberVisit };
}
